// src/components/common/LeftSideBar.tsx

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import DashboardIcon from '@mui/icons-material/Dashboard';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { useRecoilValue } from 'recoil';
import { authState, userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';
import {
  ADMIN_MENU_LIST,
  PARTNER_MENU_LIST,
  PARTNER_MENU_LIST_WITH_MANUAL_ORDER,
} from '@constants/menu';

type LeftSideBarProps = {
  /** Whether the mobile drawer is open */
  mobileOpen: boolean;
  /** Close handler for the mobile drawer */
  onClose: () => void;
  /** Drawer width for desktop layout */
  drawerWidth?: number;
};

/**
 * Check if a route path is active.
 *
 * - exact: match only when currentPath === targetPath
 * - otherwise: prefix match with a path boundary ("/products" matches "/products/123")
 */
const isPathActive = (currentPath: string, targetPath: string, exact?: boolean): boolean => {
  if (!targetPath) return false;

  if (exact) return currentPath === targetPath;

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

/**
 * Global left sidebar (navigation drawer).
 *
 * - Selects menu set based on user role (Admin vs Partner).
 * - Supports mobile (temporary drawer) + desktop (permanent drawer).
 * - Highlights active route and expands/collapses menu groups.
 */
const LeftSideBar = ({ mobileOpen, onClose, drawerWidth = 240 }: LeftSideBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const user = useRecoilValue(userState);
  const isAuthenticated = useRecoilValue(authState);

  /**
   * Pick the base menu list based on the authenticated user role.
   */
  const menuList = useMemo(() => {
    if (!user || isAuthenticated !== true) return [];

    const { group } = user;

    if (
      group === UserGroup.ADMINISTRATOR ||
      group === UserGroup.PRODUCT_MANAGER ||
      group === UserGroup.MANAGER
    ) {
      return ADMIN_MENU_LIST;
    }

    if (group === UserGroup.PARTNER) {
      return user.manualOrderEnabled ? PARTNER_MENU_LIST_WITH_MANUAL_ORDER : PARTNER_MENU_LIST;
    }

    return [];
  }, [user, isAuthenticated]);

  /**
   * Usage-based priority order for top-level menu groups.
   * Keeps the sidebar consistent across Admin/Partner configurations.
   */
  const orderedMenuList = useMemo(() => {
    const ORDER_KEYWORDS = ['home', 'order', 'product', 'stock', 'partner', 'user'];

    const getOrderIndex = (menuName: string) => {
      const lower = menuName.toLowerCase().trim();
      const idx = ORDER_KEYWORDS.findIndex((key) => lower.includes(key));
      return idx === -1 ? ORDER_KEYWORDS.length : idx;
    };

    return [...menuList].sort((a, b) => {
      const aOrder = getOrderIndex(a.menu);
      const bOrder = getOrderIndex(b.menu);

      if (aOrder !== bOrder) return aOrder - bOrder;

      // Tie-breaker: alphabetical for stable ordering
      return a.menu.localeCompare(b.menu);
    });
  }, [menuList]);

  /**
   * Expanded/collapsed state for each parent menu group.
   */
  const [menuState, setMenuState] = useState<Record<string, boolean>>({});

  /**
   * Default behavior: expand all groups on initial load (and when menu set changes).
   */
  useEffect(() => {
    const next: Record<string, boolean> = {};
    orderedMenuList.forEach((m) => {
      next[m.menu] = true;
    });
    setMenuState(next);
  }, [orderedMenuList]);

  /**
   * Parent menu click:
   * - Toggle expand/collapse
   * - Optionally navigate to the "default" submenu (first child) when collapsed -> expanded
   */
  const handleParentClick = (menuName: string, defaultPath?: string) => {
    setMenuState((prev) => ({ ...prev, [menuName]: !prev[menuName] }));

    if (defaultPath) {
      navigate(defaultPath);
      onClose();
    }
  };

  /**
   * Submenu click: navigate and close mobile drawer.
   */
  const handleSubClick = (path: string) => {
    navigate(path);
    onClose();
  };

  /**
   * Map menu group name to an icon.
   */
  const getMenuIcon = (menuName: string) => {
    const key = menuName.toLowerCase().trim();

    if (key === 'home' || key.includes('dashboard')) return <DashboardIcon />;
    if (key.includes('partner')) return <HandshakeIcon />;
    if (key.includes('user')) return <PeopleAltIcon />;
    if (key.includes('product')) return <StorefrontIcon />;
    if (key.includes('stock')) return <WarehouseIcon />;
    if (key.includes('order')) return <ReceiptLongIcon />;

    return <SettingsIcon />;
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar />

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List sx={{ py: 0 }}>
          {orderedMenuList.map((menu) => {
            const hasSubMenu = Array.isArray(menu.subMenu) && menu.subMenu.length > 0;

            // Use first submenu as the default route for parent click (if any)
            const defaultPath = hasSubMenu ? menu.subMenu[0].path : undefined;

            // Highlight parent if any child is active
            const hasActiveChild =
              hasSubMenu &&
              menu.subMenu.some((sub) => isPathActive(location.pathname, sub.path, sub.exact));

            const isParentActive = hasActiveChild;

            return (
              <Fragment key={`menu-${menu.menu}`}>
                {/* Parent menu item */}
                <ListItem
                  disablePadding
                  sx={{
                    backgroundColor: isParentActive ? 'action.selected' : 'background.paper',
                    mb: 0.5,
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemButton
                    onClick={() => handleParentClick(menu.menu, defaultPath)}
                    sx={{
                      px: 2,
                      py: 1.2,
                      '&:hover': { backgroundColor: 'action.hover' },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isParentActive ? 'primary.main' : 'text.secondary',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {getMenuIcon(menu.menu)}
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.98rem',
                            color: isParentActive ? 'text.primary' : 'text.secondary',
                          }}
                        >
                          {menu.menu}
                        </Typography>
                      }
                    />

                    {/* Expand/collapse indicator */}
                    {hasSubMenu && (menuState[menu.menu] ? <ExpandLess /> : <ExpandMore />)}
                  </ListItemButton>
                </ListItem>

                {/* Submenu items */}
                {hasSubMenu &&
                  menuState[menu.menu] &&
                  menu.subMenu.map((sub) => {
                    const isActive = isPathActive(location.pathname, sub.path, sub.exact);

                    return (
                      <ListItem key={`${menu.menu}-${sub.name}`} disablePadding sx={{ mb: 0.2 }}>
                        <ListItemButton
                          onClick={() => handleSubClick(sub.path)}
                          sx={{
                            pl: 5.5,
                            pr: 2,
                            py: 0.9,
                            borderLeft: isActive ? 3 : 0,
                            borderColor: 'primary.main',
                            '&:hover': { backgroundColor: 'action.hover' },
                            backgroundColor: isActive ? 'action.selected' : 'inherit',
                            transition: 'background-color 0.2s ease, border-color 0.2s ease',
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: '0.9rem',
                                  color: isActive ? 'primary.main' : 'text.primary',
                                }}
                              >
                                {sub.name}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
              </Fragment>
            );
          })}
        </List>
      </Box>

      <Divider />
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="sidebar navigation"
    >
      {/* Mobile: temporary drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }} // Better performance on mobile
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'background.default',
            color: 'text.primary',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop: permanent drawer */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'background.default',
            color: 'text.primary',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default LeftSideBar;
